import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CategorySelectionDialogProps {
  open: boolean;
  onClose: () => void;
  categories: Record<string, string>;
  onConfirm: (selectedCategory: string) => void;
  parents?: Record<string, string>;
}

export function CategorySelectionDialog({ open, onClose, categories, onConfirm, parents = {} }: CategorySelectionDialogProps) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('');

  const categoryKeys = Object.keys(categories).sort((a, b) => {
    const label = (id: string) => `${parents[id] ? `${categories[parents[id]] || parents[id]}/` : ''}${categories[id] || id}`;
    return label(a).localeCompare(label(b), undefined, { sensitivity: 'base' });
  });

  useEffect(() => {
    if (open && categoryKeys.length > 0) {
      setSelectedCategory(categoryKeys[0]);
    }
  }, [open, categories]);

  const handleConfirm = () => {
    if (selectedCategory) {
      onConfirm(selectedCategory);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
        {t('entity_list.codex.new_entry_dialog.title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('entity_list.codex.new_entry_dialog.description')}
        </Typography>
        <FormControl fullWidth>
          <RadioGroup
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categoryKeys.map((catId) => (
              <FormControlLabel
                key={catId}
                value={catId}
                control={<Radio color="primary" />}
                label={parents[catId] ? `${categories[parents[catId]] || parents[catId]}/${categories[catId] || catId}` : (categories[catId] || catId)}
                sx={{
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  margin: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                  flexDirection: 'row-reverse',
                  '& .MuiTypography-root': {
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          {t('entity_list.codex.new_entry_dialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{ borderRadius: 2 }}
          disabled={!selectedCategory}
        >
          {t('entity_list.codex.new_entry_dialog.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
